import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Satellite, Wifi, Gauge, Camera, CheckCircle2, AlertTriangle, Wrench } from 'lucide-react'
import api from '../api'
import { Button, Alert, Label, Card } from '../components/ui'
import useBranding from '../useBranding'

const textareaStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 10, minHeight: 80, resize: 'vertical',
  border: '1px solid var(--border-strong)', fontSize: 13.5, color: 'var(--text)',
  background: 'var(--surface)', outline: 'none', fontFamily: 'inherit'
}

const selectStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 10,
  border: '1px solid var(--border-strong)', fontSize: 13.5, color: 'var(--text)',
  background: 'var(--surface)', outline: 'none', fontFamily: 'inherit'
}

function Shell({ children }) {
  const businessName = useBranding()
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(circle at 20% 20%, #2b2f77 0%, #14152b 55%, #0b0b16 100%)',
      padding: 20
    }}>
      <div style={{ width: 420, maxWidth: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, margin: '0 auto 12px',
            background: 'linear-gradient(135deg, var(--brand), #7c8cf8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(79,70,229,0.35)'
          }}>
            <Satellite size={22} color="#fff" strokeWidth={2} />
          </div>
          <h1 style={{ fontSize: 19, fontWeight: 800, color: '#fff', letterSpacing: -0.4 }}>{businessName} — Tiket Gangguan</h1>
        </div>
        <Card style={{ padding: 28 }}>{children}</Card>
      </div>
    </div>
  )
}

export default function TiketForm() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tiket, setTiket] = useState(null)
  const [selectedNama, setSelectedNama] = useState([])
  const [jenisPekerjaan, setJenisPekerjaan] = useState('GNG')
  const [catatan, setCatatan] = useState('')
  const [foto, setFoto] = useState(null)
  const [submittingSelesai, setSubmittingSelesai] = useState(false)
  const [submittingProses, setSubmittingProses] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [prosesInfo, setProsesInfo] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => { loadTiket() }, [token])

  function loadTiket() {
    setLoading(true)
    api.get(`/tiket/${token}`)
      .then(res => setTiket(res.data))
      .catch(err => setError(err.response?.data?.message || 'Tiket tidak ditemukan'))
      .finally(() => setLoading(false))
  }

  function toggleNama(nama) {
    setSelectedNama(s => s.includes(nama) ? s.filter(n => n !== nama) : [...s, nama])
  }

  async function handleSelesai(e) {
    e.preventDefault()
    setSubmitError(''); setProsesInfo('')
    if (selectedNama.length === 0) return setSubmitError('Pilih minimal satu teknisi')
    if (!foto) return setSubmitError('Foto bukti perbaikan wajib dilampirkan')

    setSubmittingSelesai(true)
    try {
      const fd = new FormData()
      fd.append('nama', selectedNama.join(','))
      fd.append('jenisPekerjaan', jenisPekerjaan)
      fd.append('catatan', catatan)
      fd.append('foto', foto)
      await api.post(`/tiket/${token}/selesai`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setDone(true)
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Gagal menyimpan laporan')
    } finally { setSubmittingSelesai(false) }
  }

  async function handleProses() {
    setSubmitError(''); setProsesInfo('')
    if (selectedNama.length === 0) return setSubmitError('Pilih minimal satu teknisi')
    if (!catatan.trim()) return setSubmitError('Isi catatan kendala/kebutuhan dulu sebelum tandai Proses')

    setSubmittingProses(true)
    try {
      const fd = new FormData()
      fd.append('nama', selectedNama.join(','))
      fd.append('catatan', catatan)
      if (foto) fd.append('foto', foto)
      await api.post(`/tiket/${token}/proses`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setProsesInfo('Status "Proses" tersimpan — lanjutkan isi "Tandai Selesai" di bawah kalau sudah beres.')
      setCatatan('')
      setFoto(null)
      loadTiket()
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Gagal menyimpan')
    } finally { setSubmittingProses(false) }
  }

  if (loading) return <Shell><p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Memuat tiket...</p></Shell>

  if (error) return (
    <Shell>
      <div style={{ textAlign: 'center' }}>
        <AlertTriangle size={32} color="var(--danger)" style={{ marginBottom: 10 }} />
        <p style={{ color: 'var(--text)', fontWeight: 600 }}>{error}</p>
      </div>
    </Shell>
  )

  if (tiket.status === 'DIABAIKAN') return (
    <Shell>
      <div style={{ textAlign: 'center' }}>
        <AlertTriangle size={32} color="var(--text-muted)" style={{ marginBottom: 10 }} />
        <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>Tiket ini sudah dibatalkan otomatis.</p>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>Ambang redaman diubah setelah tiket ini dibuat, dan redaman pelanggan ini sudah dianggap wajar — tidak perlu ditindaklanjuti lagi.</p>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>Pelanggan: {tiket.username}</p>
      </div>
    </Shell>
  )

  if (done || tiket.status === 'SELESAI') return (
    <Shell>
      <div style={{ textAlign: 'center' }}>
        <CheckCircle2 size={36} color="var(--success)" style={{ marginBottom: 10 }} />
        <p style={{ color: 'var(--text)', fontWeight: 700, fontSize: 15 }}>
          {done ? 'Laporan tersimpan, terima kasih!' : 'Tiket ini sudah pernah diselesaikan sebelumnya.'}
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>Pelanggan: {tiket.username}</p>
      </div>
    </Shell>
  )

  return (
    <Shell>
      <div style={{ marginBottom: 20, padding: 14, borderRadius: 12, background: 'var(--danger-soft)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontWeight: 700, color: 'var(--danger)' }}>
          <Wifi size={16} /> {tiket.username}
        </div>
        {tiket.sumber === 'MANUAL' ? (
          <p style={{ fontSize: 13, color: 'var(--text)', margin: 0 }}>{tiket.keluhan}</p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
            <Gauge size={14} /> Redaman: <strong style={{ color: 'var(--text)' }}>{tiket.redaman} dBm</strong>
            &nbsp;·&nbsp; IP: {tiket.ip} &nbsp;·&nbsp; OLT: {tiket.olt === 'hioso' ? 'Hioso' : tiket.olt === 'hsgq' ? 'HSGQ' : tiket.olt} ({tiket.port})
          </div>
        )}
      </div>

      {tiket.status === 'PROSES' && (
        <div style={{ marginBottom: 20, padding: 14, borderRadius: 12, background: 'var(--warning-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontWeight: 700, color: 'var(--warning)' }}>
            <Wrench size={16} /> Sedang Diproses
          </div>
          <p style={{ fontSize: 13, color: 'var(--text)', margin: 0 }}>{tiket.catatanProses}</p>
          <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>Update terakhir: {tiket.prosesAt}</p>
        </div>
      )}

      {prosesInfo && <Alert type="success">{prosesInfo}</Alert>}

      <form onSubmit={handleSelesai}>
        {submitError && <Alert type="error">{submitError}</Alert>}

        <div style={{ marginBottom: 16 }}>
          <Label>Teknisi yang mengerjakan</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            {tiket.teknisi.map(t => {
              const active = selectedNama.includes(t.nama)
              return (
                <button
                  type="button" key={t.id} onClick={() => toggleNama(t.nama)}
                  style={{
                    padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    border: active ? '1px solid var(--brand)' : '1px solid var(--border-strong)',
                    background: active ? 'var(--brand-soft)' : 'transparent',
                    color: active ? 'var(--brand-soft-text)' : 'var(--text-muted)'
                  }}
                >{t.nama}</button>
              )
            })}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Label>Jenis pekerjaan</Label>
          <select
            style={{ ...selectStyle, marginTop: 6 }}
            value={jenisPekerjaan} onChange={e => setJenisPekerjaan(e.target.value)}
          >
            {tiket.jenisPekerjaan.map(j => (
              <option key={j.kode} value={j.kode}>{j.kode} — {j.nama}</option>
            ))}
          </select>
          <p style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>Cuma dipakai kalau tandai "Selesai" di bawah.</p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Label>Catatan</Label>
          <textarea
            style={{ ...textareaStyle, marginTop: 6 }}
            value={catatan} onChange={e => setCatatan(e.target.value)}
            placeholder="Mis. ganti konektor SC/APC, bersihkan splitter, dll. — atau kalau belum selesai, jelaskan kendala/alat yang dibutuhkan"
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <Label>Foto {'(wajib kalau "Selesai", opsional kalau "Proses")'}</Label>
          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            marginTop: 6, padding: '16px', borderRadius: 10, cursor: 'pointer',
            border: '1.5px dashed var(--border-strong)', color: 'var(--text-muted)', fontSize: 13
          }}>
            <Camera size={18} />
            {foto ? foto.name : 'Ambil / pilih foto'}
            <input
              type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }}
              onChange={e => setFoto(e.target.files[0] || null)}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            type="button" variant="neutral" loading={submittingProses} disabled={submittingSelesai}
            onClick={handleProses} style={{ flex: 1, padding: '12px' }}
          >
            {submittingProses ? 'Menyimpan...' : 'Tandai Proses'}
          </Button>
          <Button type="submit" loading={submittingSelesai} disabled={submittingProses} style={{ flex: 1, padding: '12px' }}>
            {submittingSelesai ? 'Menyimpan...' : 'Tandai Selesai'}
          </Button>
        </div>
      </form>
    </Shell>
  )
}
